/*
 * BOGOS single-gift auto-add + cart re-sync bridge
 * ------------------------------------------------------------------
 * WHY THIS EXISTS (investigated live 2026-07):
 *   This store runs two SkaiLama free-gift apps at once — BOGOS
 *   (bogos-io-free-gift) and Kite (kite-discount-free-gift). Kite powers
 *   the "TODAS POR R$ 99" custom discount and MUST keep working; BOGOS
 *   owns the free-gift offers.
 *
 *   BOGOS's gift is auto-added by its LEGACY engine, which only activates
 *   when it detects a supported cart app (e.g. UpCart). On the live store
 *   (UpCart) that engine loads the offer (FGSECOMAPP.offers === 1) and the
 *   gift is added automatically. On this theme's NATIVE cart, BOGOS detects
 *   no supported cart app, its legacy engine stays inert (offers === 0),
 *   and the gift is never added. That is the whole bug: "works on UpCart,
 *   breaks on the native cart".
 *
 *   Instead of forcing two minified apps to coexist in one global
 *   namespace, this script reproduces ONLY the auto-add gift rule in the
 *   theme. It never touches Kite's R$99 discount (a server-side Shopify
 *   Function), so both promotions work together.
 *
 * WHAT IT DOES:
 *   - Resolves the gift variant from its product handle, read from BOGOS's
 *     live offer data when present, else from GIFTS[].handles.
 *   - After any cart mutation (and on load) it checks whether the cart
 *     qualifies: at/above the threshold it ensures exactly ONE gift line;
 *     below it, it removes the gift. The gift product is natively R$0.
 *     The threshold can be a minimum item QUANTITY or a minimum cart VALUE
 *     (R$) depending on how the offer is configured in BOGOS.
 *   - Re-pulls the theme's custom drawer from the server (cart:refresh),
 *     because the drawer paints from a snapshot in its own AJAX response
 *     and never re-reads it otherwise.
 *
 * 2026-09-15 / 09-17 / 09-18: porta-joias offers (money >= R$600, and a
 *   link-gated 3-peças variant running at the same time). Both are gone.
 *
 * 2026-10-01: BOGOS offer 212645 re-used as "Brinde Misterioso" — 6+ pieces
 *   (unit "number_of_products", any product), one gift, single variant
 *   (clone handle brinde-misterioso-sca_clone_freegift), no link
 *   sub-condition. Confirmed by decoding BOGOS's packed fgData bundle.
 *   Changes:
 *   - Gift scoping moved from a hardcoded "porta-joia" to GIFT_KEYWORDS.
 *     It must stay offer-specific, never the bare "sca_clone_freegift"
 *     suffix: the colar-de-letra picker (assets/bogos-letter-necklace-gift.js)
 *     shares that suffix, and a generic match silently auto-added its gift
 *     (live bug 2026-08-11).
 *   - Rules are read from window.fgData at reconcile time, not at script
 *     start. The BOGOS bundle is a deferred script placed after this one, so
 *     window.fgData is still undefined when this file runs (found
 *     2026-09-25). The first reconcile now waits for DOMContentLoaded.
 *   - Handles are read from gift_products[].clone_product_handle. The old
 *     `"handle":"..."` regex never matched BOGOS's blob.
 *   - The link-gated rule only exists while a live offer carries a
 *     sub_conditions.specific_link_address. The old hardcoded 3-peças default
 *     would otherwise still give the gift at 3 pieces to anyone cached as
 *     eligible from the old porta-joias campaign link.
 *
 * 2026-10-08: a second offer runs alongside it — 218934 "Porta-joias de
 *   brinde - Bundle": 1+ unit of the BYOB product "Bundle - 6 peças por R$89
 *   cada" (product_narrow include) gives porta-joias-para-viagem-murano-
 *   sca_clone_freegift. Brinde Misterioso now EXCLUDES that same Bundle from
 *   its 6-piece count. Changes:
 *   - GIFTS holds one entry per offer; each gift is resolved, counted and
 *     added/removed on its own, so both can be in the cart at once.
 *   - product_narrow is applied (only lines it includes / doesn't exclude
 *     count). The BYOB bundle (discount type PR) is added as ONE line of the
 *     Bundle product, quantity 1, so it counts as 1 piece.
 *   - Every BOGOS clone-gift line (any offer) is left out of the counts.
 *
 * SAFETY: idempotent (only ever keeps qty 1 of the gift; if BOGOS ever
 *   starts adding the same variant, it is not duplicated); the reconcile is
 *   guarded so it cannot overlap or loop.
 */
(function () {
  if (window.__bogosCartSyncInstalled) return;
  window.__bogosCartSyncInstalled = true;

  /* ---- Offer config (mirrors the live BOGOS single-gift offers) ---- */
  // One entry per offer. Only clone-gift handles containing `keyword` belong
  // to that entry. `handles` and `fallback` are used only when the offer
  // isn't in window.fgData (bundle failed to load); both match the live
  // BOGOS config decoded 2026-10-08.
  var BUNDLE_NARROW = { "15415120625918": [] }; // "Bundle - 6 peças por R$89 cada"
  var GIFTS = [
    {
      keyword: "brinde-misterioso", // offer 212645
      handles: ["brinde-misterioso-sca_clone_freegift"],
      fallback: { mode: "quantity", qty: 6, moneyCents: 0, narrow: { include: false, products: BUNDLE_NARROW } },
    },
    {
      keyword: "porta-joias-para-viagem-murano", // offer 218934
      handles: ["porta-joias-para-viagem-murano-sca_clone_freegift"],
      fallback: { mode: "quantity", qty: 1, moneyCents: 0, narrow: { include: true, products: BUNDLE_NARROW } },
    },
    {
      keyword: "porta-joias-murano-em-veludo", // offer 299144 "Porta-joias de brinde - Black November" (2026-11-02)
      handles: ["porta-joias-murano-em-veludo-sca_clone_freegift"],
      fallback: { mode: "quantity", qty: 10, moneyCents: 0, narrow: null },
    },
  ];
  var CLONE_SUFFIX = "sca_clone_freegift";

  GIFTS.forEach(function (gift) {
    gift.variantId = null; // resolved lazily from a candidate handle
    gift.handle = null; // the handle that actually resolved
    gift.resolved = false;
  });

  function isOwnGiftHandle(gift, handle) {
    return typeof handle === "string" &&
      handle.indexOf(CLONE_SUFFIX) !== -1 &&
      handle.indexOf(gift.keyword) !== -1;
  }

  // The BOGOS offers whose gift products belong to `gift`.
  function liveOffers(gift) {
    try {
      var offers = (window.fgData && window.fgData.offers) || [];
      return offers.filter(function (o) {
        return (o && o.gift_products || []).some(function (g) {
          return g && isOwnGiftHandle(gift, g.clone_product_handle);
        });
      });
    } catch (e) {
      return [];
    }
  }

  // BOGOS's product_narrow: the condition only counts the listed products
  // ("include") or everything but them ("exclude"). products maps product
  // id → listed variant ids (empty = any variant). Only "variants_ids" is
  // readable from cart JSON; any other type (e.g. collections) is marked
  // unsupported — an include then never qualifies rather than counting
  // the whole cart.
  function narrowFromCondition(cond) {
    var n = cond.product_narrow && cond.product_narrow[0];
    if (!n) return null;
    var include = n.filter_option !== "exclude";
    if (n.type !== "variants_ids" || !n.value) return { include: include, unsupported: true };
    var products = {};
    Object.keys(n.value).forEach(function (pid) {
      products[pid] = ((n.value[pid] && n.value[pid].variants) || []).map(function (v) { return String(v.id); });
    });
    return { include: include, products: products };
  }

  function ruleFromOffer(o) {
    var cond = o.offer_conditions && o.offer_conditions[0];
    var amount = cond ? parseFloat(cond.min_amount) : NaN;
    if (!(amount > 0)) return null;
    var narrow = narrowFromCondition(cond);
    if (cond.unit === "money") return { mode: "money", qty: 0, moneyCents: Math.round(amount * 100), narrow: narrow };
    return { mode: "quantity", qty: amount, moneyCents: 0, narrow: narrow };
  }

  // Two independent qualifying paths can each grant the gift (OR, not AND):
  //   general — the offer without a sub_conditions.specific_link_address;
  //   link    — the offer with one, also gated on isEligible().
  // With BOGOS data on the page, only the rules it actually defines exist.
  function currentRules(gift) {
    var offers = liveOffers(gift);
    if (!offers.length) {
      // 2026-11-02: when BOGOS data loaded but this gift's offer isn't in it,
      // the offer is off — give nothing. Fallback only when the data is missing.
      var bogosLoaded = !!(window.fgData && window.fgData.offers);
      return { general: bogosLoaded ? null : gift.fallback, link: null };
    }
    var rules = { general: null, link: null };
    offers.forEach(function (o) {
      var rule = ruleFromOffer(o);
      if (!rule) return;
      var linkValue = o.sub_conditions && o.sub_conditions.specific_link_address;
      if (linkValue) {
        rule.linkValue = linkValue;
        rules.link = rule;
      } else {
        rules.general = rule;
      }
    });
    return rules;
  }

  // If the campaign-link param is present, cache eligibility in localStorage
  // so it survives navigation away from the landing page; otherwise fall
  // back to whatever was cached before. Keyed by the link value, so an old
  // campaign's eligibility never carries over to a new one.
  var LINK_PARAM = "freegifts_code";
  var ELIGIBLE_KEY_PREFIX = "bogosGiftLinkEligible:";

  function isEligible(linkValue) {
    if (!linkValue) return false;
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get(LINK_PARAM) === linkValue) {
        localStorage.setItem(ELIGIBLE_KEY_PREFIX + linkValue, "1");
        return true;
      }
      return localStorage.getItem(ELIGIBLE_KEY_PREFIX + linkValue) === "1";
    } catch (e) {
      return false;
    }
  }

  // Cache link eligibility on the landing page itself. The link param can
  // be seen before BOGOS data loads, so cache any value present; it only
  // grants anything once a live offer uses that same value.
  try {
    var landingLink = new URLSearchParams(window.location.search).get(LINK_PARAM);
    if (landingLink) isEligible(landingLink);
  } catch (e) {}

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  var MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i;
  var DEBOUNCE_MS = 550;

  var timer = null;
  var reconciling = false;
  var selfMutating = false; // true only while WE add/remove the gift

  function pathnameOf(url) {
    try { return new URL(url, window.location.origin).pathname; }
    catch (e) { return String(url || "").split("?")[0].split("#")[0]; }
  }
  function isCartMutation(url) {
    if (!url) return false;
    return MUTATION_RE.test(pathnameOf(url)) || MUTATION_RE.test(String(url));
  }

  function discoverHandlesFromFgData(gift) {
    var found = [];
    liveOffers(gift).forEach(function (o) {
      (o.gift_products || []).forEach(function (g) {
        var h = g && g.clone_product_handle;
        if (isOwnGiftHandle(gift, h) && found.indexOf(h) === -1) found.push(h);
      });
    });
    return found;
  }

  function tryHandle(handle) {
    return fetch(ROOT + "products/" + handle + ".js", { headers: { Accept: "application/json" }, cache: "no-store", credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { return p && p.variants && p.variants[0] ? p : null; })
      .catch(function () { return null; });
  }

  // Resolve the gift variant by trying discovered + known handles in order and
  // keeping the first that maps to a real product on this store.
  function resolveGiftVariant(gift) {
    if (gift.resolved) return Promise.resolve(gift.variantId);
    var candidates = discoverHandlesFromFgData(gift).concat(gift.handles);
    var seen = {};
    candidates = candidates.filter(function (h) { return h && !seen[h] && (seen[h] = 1); });

    var i = 0;
    function next() {
      if (i >= candidates.length) { gift.resolved = true; return gift.variantId; }
      var handle = candidates[i++];
      return tryHandle(handle).then(function (p) {
        if (p) {
          gift.handle = p.handle || handle;
          gift.variantId = p.variants[0].id;
          gift.resolved = true;
          return gift.variantId;
        }
        return next();
      });
    }
    return Promise.resolve(next());
  }

  function getCart() {
    return fetch(ROOT + "cart.js", { headers: { Accept: "application/json" }, cache: "no-store", credentials: "same-origin" })
      .then(function (r) { return r.json(); });
  }

  function addGift(gift) {
    selfMutating = true;
    return fetch(ROOT + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: gift.variantId, quantity: 1 }),
    }).finally(function () { selfMutating = false; });
  }

  function setGiftQty(lineKey, qty) {
    selfMutating = true;
    return fetch(ROOT + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: lineKey, quantity: qty }),
    }).finally(function () { selfMutating = false; });
  }

  function isGiftItem(gift, item) {
    if (gift.handle && item.handle === gift.handle) return true;
    if (gift.variantId && (item.variant_id === gift.variantId || item.id === gift.variantId)) return true;
    // Also treat a stale clone line of this offer as the gift (e.g. left by
    // a previous version of the offer), so it gets removed below threshold
    // instead of counted.
    return isOwnGiftHandle(gift, item.handle);
  }

  // Free gift lines of ANY BOGOS offer (ours, colar-de-letra...) never count
  // towards a threshold.
  function isAnyCloneGift(item) {
    return typeof item.handle === "string" && item.handle.indexOf(CLONE_SUFFIX) !== -1;
  }

  function narrowMatches(narrow, item) {
    var variants = narrow.products[String(item.product_id)];
    if (!variants) return false;
    return variants.length === 0 || variants.indexOf(String(item.variant_id)) !== -1;
  }

  function counts(narrow, item) {
    if (!narrow) return true;
    if (narrow.unsupported) return !narrow.include;
    return narrowMatches(narrow, item) === narrow.include;
  }

  // Money sums final_line_price: on this store it ignores discount codes, so
  // a coupon can't un-earn the gift (verified 2026-09-20).
  function meets(rule, items) {
    if (!rule) return false;
    var qty = 0;
    var cents = 0;
    items.forEach(function (it) {
      if (!counts(rule.narrow, it)) return;
      qty += it.quantity;
      cents += it.final_line_price || 0;
    });
    return rule.mode === "money" ? cents >= rule.moneyCents : qty >= rule.qty;
  }

  // Bring one gift in line with its offer rule. Resolves to true if changed.
  function reconcileOneGift(gift, items, paidItems) {
    if (!gift.variantId) return Promise.resolve(false); // gift product didn't resolve → do nothing
    var giftLines = items.filter(function (it) { return isGiftItem(gift, it); });
    var rules = currentRules(gift);
    var meetsGeneral = meets(rules.general, paidItems);
    var meetsLink = meets(rules.link, paidItems) && isEligible(rules.link.linkValue);
    var qualifies = meetsGeneral || meetsLink;

    var giftLine = giftLines[0] || null;
    // Never allow more than one gift line/unit at a time — a duplicate
    // line can appear from a race (two near-simultaneous adds) and the
    // native cart's own quantity stepper can bump a single line above
    // 1. Both are corrected here regardless of `qualifies`, same
    // "max 1 gift" rule applied to the colar-de-letra bridge.
    var extras = giftLines.slice(1);
    var dedupe = extras.reduce(function (p, extra) {
      return p.then(function () { return setGiftQty(extra.key, 0); });
    }, Promise.resolve());

    return dedupe.then(function () {
      if (qualifies && !giftLine) return addGift(gift).then(function () { return true; });
      if (!qualifies && giftLine) return setGiftQty(giftLine.key, 0).then(function () { return true; });
      if (qualifies && giftLine && giftLine.quantity !== 1) return setGiftQty(giftLine.key, 1).then(function () { return true; });
      return extras.length > 0;
    });
  }

  // Bring the cart in line with every offer rule. Resolves to true if changed.
  // Gifts are handled one after another (never in parallel) to avoid
  // concurrent writes to the same cart.
  function reconcileGift() {
    if (reconciling) return Promise.resolve(false);
    reconciling = true;
    return Promise.all(GIFTS.map(resolveGiftVariant))
      .then(function (vids) {
        if (!vids.some(Boolean)) return false; // no gift product resolved → do nothing
        return getCart().then(function (cart) {
          var items = (cart && cart.items) || [];
          var paidItems = items.filter(function (it) { return !isAnyCloneGift(it); });
          return GIFTS.reduce(function (p, gift) {
            return p.then(function (changed) {
              return reconcileOneGift(gift, items, paidItems).then(function (c) { return changed || c; });
            });
          }, Promise.resolve(false));
        });
      })
      .catch(function () { return false; })
      .finally(function () { reconciling = false; });
  }

  function runSync() {
    // Only dispatch when something was actually changed (added/removed) —
    // an unconditional dispatch here forces the cart drawer to tear down and
    // rebuild ~550ms after every mutation even when nothing changed, racing
    // with the separate colar-de-letra picker's own re-render and causing it
    // to flash and disappear (found live 2026-08-11).
    reconcileGift().then(function (changed) {
      if (changed) document.dispatchEvent(new CustomEvent("cart:refresh"));
    });
  }

  function schedule() {
    if (selfMutating) return; // ignore our own gift writes
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () { timer = null; runSync(); }, DEBOUNCE_MS);
  }

  /* ---- Patch fetch ---- */
  if (typeof window.fetch === "function") {
    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url);
      var mutation = isCartMutation(url);
      var result = originalFetch.apply(this, arguments);
      if (mutation && result && typeof result.then === "function") result.then(schedule, schedule);
      return result;
    };
  }

  /* ---- Patch XMLHttpRequest ---- */
  if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
    var proto = window.XMLHttpRequest.prototype;
    var originalOpen = proto.open;
    proto.open = function (method, url) {
      this.__bogosIsCartMutation = isCartMutation(url);
      return originalOpen.apply(this, arguments);
    };
    var originalSend = proto.send;
    proto.send = function () {
      if (this.__bogosIsCartMutation) this.addEventListener("loadend", schedule);
      return originalSend.apply(this, arguments);
    };
  }

  /* ---- Reconcile once on load ----
   * Deferred scripts run before DOMContentLoaded, in document order, so by
   * then the BOGOS bundle (deferred, later in the page) has set fgData. */
  if (document.readyState === "complete") {
    runSync();
  } else {
    document.addEventListener("DOMContentLoaded", runSync);
  }
})();
